/**
 * ChatInput Component
 * 
 * Chat input area with:
 * - Sample request button
 * - Text input with Enter key support
 * - Photo upload button (with consent check)
 * - Send button
 * - Web search button
 * - Voice mode button (OpenAI Realtime API)
 * 
 * @author Heseda AI Team
 * @version 1.1
 */

import React, { useRef, useState, useEffect } from 'react';
import VoiceButton from './VoiceButton';
import type { VoiceSessionState } from '../../services/voiceService';
import { trackPageView } from '../../services/activityTrackingService';
import { logEvent } from '../../services/eventTrackingService';
import { auth } from '../../services/firebaseConfig';
import { SAMPLE_FEATURE_ENABLED } from '../../config/adminConfig';

interface ChatInputProps {
  language: string;
  input: string;
  isLoading: boolean;
  isSearching: boolean;
  hasPhotoConsent: boolean;
  sampleRequested: boolean;
  /** Whether user is in Phase 1 (profile completion < 50%, search NOT available) */
  isPhase1: boolean;
  /** Current profile completion percentage (0-100) — shown in Phase 1 search tooltip */
  profileCompletion?: number;
  /** Seconds remaining until rate-limit cooldown expires (0 = no limit) */
  rateLimitCooldown?: number;
  /** True when daily AI budget is 0 (proactive block before 429) */
  dailyAiExhausted?: boolean;
  /** True when daily search budget is 0 */
  dailySearchExhausted?: boolean;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  onInputChange: (value: string) => void;
  onSend: () => void;
  onSearch: () => void;
  onRequestSample: () => void;
  onImageChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onPhotoClick: () => void;
  /** Callback to open Buy More / token purchase modal */
  onBuyMore?: () => void;
  /** Voice mode props */
  voiceState?: VoiceSessionState;
  isVoiceSupported?: boolean;
  onVoiceToggle?: () => void;
}

/**
 * Reset iOS Safari viewport zoom after input blur
 * iOS Safari may zoom in on input focus despite font-size >= 16px
 */
const resetViewportZoom = () => {
  // Small delay to allow the keyboard to dismiss first
  setTimeout(() => {
    // Force scroll to reset any zoom
    window.scrollTo(0, 0);
    
    // Update viewport meta to force reset (iOS Safari specific)
    const viewportMeta = document.querySelector('meta[name="viewport"]');
    if (viewportMeta) {
      const content = viewportMeta.getAttribute('content') || '';
      // Toggle maximum-scale to force viewport reset
      viewportMeta.setAttribute('content', content.replace('maximum-scale=1', 'maximum-scale=0.99'));
      requestAnimationFrame(() => {
        viewportMeta.setAttribute('content', content);
      });
    }
  }, 100);
};

/**
 * Chat input area with message input, photo upload, voice mode, and action buttons
 */
function ChatInput({
  language,
  input,
  isLoading,
  isSearching,
  hasPhotoConsent,
  sampleRequested,
  isPhase1,
  profileCompletion = 0,
  rateLimitCooldown = 0,
  dailyAiExhausted = false,
  dailySearchExhausted = false,
  fileInputRef,
  onInputChange,
  onSend,
  onSearch,
  onRequestSample,
  onImageChange,
  onPhotoClick,
  onBuyMore,
  voiceState,
  isVoiceSupported = false,
  onVoiceToggle,
}: ChatInputProps) {
  // Determine if voice mode is active
  const isVoiceActive = voiceState && ['connected', 'listening', 'speaking'].includes(voiceState.status);
  const searchBtnRef = useRef<HTMLButtonElement>(null);
  const textInputRef = useRef<HTMLInputElement>(null);
  const [showSearchTip, setShowSearchTip] = useState(false);
  const searchTipTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Re-focus input after AI finishes responding (isLoading false → input empty)
  const prevLoadingRef = useRef(isLoading);
  useEffect(() => {
    if (prevLoadingRef.current && !isLoading) {
      // AI just finished — refocus the text input (skip on mobile to avoid keyboard pop)
      if (window.innerWidth > 768) {
        textInputRef.current?.focus();
      }
    }
    prevLoadingRef.current = isLoading;
  }, [isLoading]);

  // Cleanup search-tip timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTipTimerRef.current) clearTimeout(searchTipTimerRef.current);
    };
  }, []);

  // Voice tooltip: show once for new users, dismiss on click or after 8 seconds

  // Helper: log user message event
  const logUserMessage = () => {
    const uid = auth.currentUser?.uid;
    const trimmedInput = input.trim();
    if (uid && trimmedInput) {
      logEvent(uid, 'user_message', { messageLength: trimmedInput.length });
    }
  };

  // Helper: log sample request event
  const logSampleRequest = () => {
    const uid = auth.currentUser?.uid;
    if (uid) {
      logEvent(uid, 'sample_requested', {});
    }
  };

  // Helper: log photo upload event
  const logPhotoUpload = () => {
    const uid = auth.currentUser?.uid;
    if (uid) {
      logEvent(uid, 'photo_uploaded', {});
    }
  };

  // Helper: log search event
  const logSearch = () => {
    const uid = auth.currentUser?.uid;
    const trimmedQuery = input.trim() || undefined;
    if (uid) {
      logEvent(uid, 'web_search', { query: trimmedQuery, usedFallbackSearch: !trimmedQuery });
    }
  };
  const [showVoiceTip, setShowVoiceTip] = useState(false);
  useEffect(() => {
    if (!isVoiceSupported || !onVoiceToggle) return;
    try {
      if (localStorage.getItem('heseda_voice_tip_shown')) return;
    } catch { return; }
    const timer = setTimeout(() => setShowVoiceTip(true), 3000);
    const hideTimer = setTimeout(() => {
      setShowVoiceTip(false);
      try { localStorage.setItem('heseda_voice_tip_shown', '1'); } catch { /* noop */ }
    }, 11000);
    return () => { clearTimeout(timer); clearTimeout(hideTimer); };
  }, [isVoiceSupported, onVoiceToggle]);

  const handleVoiceToggle = () => {
    setShowVoiceTip(false);
    try { localStorage.setItem('heseda_voice_tip_shown', '1'); } catch { /* noop */ }
    onVoiceToggle?.();
  };
  
  return (
    <div className="input-container">
      <div className="input-options">
        {/* Sample request button - only visible in Phase 1 and when not yet requested */}
        {SAMPLE_FEATURE_ENABLED && isPhase1 && !sampleRequested && (
          <button 
            onClick={() => { logSampleRequest(); onRequestSample(); }}
            className="sample-request-button"
          >
            <span className="btn-icon">📦</span>
            <span className="btn-text">
              {language !== 'ko' ? 'Request Sample' : '샘플 요청'}
            </span>
          </button>
        )}
      </div>
      
      {/* Voice mode indicator - show when voice is active */}
      {isVoiceActive && (
        <div className="voice-mode-indicator">
          <span className="voice-mode-text">
            {voiceState?.status === 'listening' 
              ? (language !== 'ko' ? '🎤 Listening...' : '🎤 듣는 중...')
              : voiceState?.status === 'speaking'
              ? (language !== 'ko' ? '🔊 Speaking...' : '🔊 말하는 중...')
              : (language !== 'ko' ? '🎙️ Voice Mode' : '🎙️ 음성 모드')
            }
          </span>
          <span className="voice-mode-hint">
            {language !== 'ko' ? 'Tap mic to stop' : '마이크를 탭하여 종료'}
          </span>
        </div>
      )}
      
      {/* Hidden file input - always available */}
      <input
        type="file"
        accept="image/*"
        onChange={onImageChange}
        style={{ display: 'none' }}
        id="image-upload"
        ref={fileInputRef}
      />
      
      {/* Photo upload button - always visible */}
      <button 
        className="input-action-button" 
        onClick={() => { logPhotoUpload(); onPhotoClick(); }}
        title={language !== 'ko' ? 'Upload Image' : '사진 업로드'}
      >
        <svg viewBox="0 0 24 24" fill="currentColor" className="input-action-icon">
          <path d="M12 15.2A3.2 3.2 0 1 0 12 8.8a3.2 3.2 0 0 0 0 6.4z"/>
          <path d="M9 2L7.17 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2h-3.17L15 2H9zm3 15c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5z"/>
        </svg>
      </button>
      
      {/* Text input and send buttons - hidden when voice mode is active */}
      {!isVoiceActive && (
        <>
          <input
            ref={textInputRef}
            type="text"
            value={input}
            onChange={(e) => onInputChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !dailyAiExhausted) {
                logUserMessage();
                onSend();
              }
            }}
            onBlur={resetViewportZoom}
            disabled={isLoading || rateLimitCooldown > 0 || dailyAiExhausted}
            maxLength={2000}
            data-testid="chat-input"
            placeholder={dailyAiExhausted
              ? (language !== 'ko' ? 'Daily limit reached — Buy More ↗' : '일일 한도 도달 — 추가 구매 ↗')
              : (language !== 'ko' ? 'Type your message...' : '메시지를 입력하세요...')}
          />
          <button onClick={() => { if (!dailyAiExhausted) logUserMessage(); (dailyAiExhausted && onBuyMore ? onBuyMore : onSend)(); }} disabled={isLoading || rateLimitCooldown > 0 || (dailyAiExhausted && !onBuyMore)} className={`send-button primary-action-button${dailyAiExhausted ? ' send-button--buy-more' : ''}`} data-testid="chat-send-btn">
            <svg viewBox="0 0 24 24" fill="currentColor" className="input-action-icon">
              <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
            </svg>
            <span className="btn-text">
              {dailyAiExhausted
                ? (language !== 'ko' ? 'Buy More' : '추가 구매')
                : rateLimitCooldown > 0
                  ? (language !== 'ko' ? `Wait ${rateLimitCooldown}s` : `${rateLimitCooldown}초 대기`)
                  : isLoading
                    ? (language !== 'ko' ? 'Sending...' : '전송 중...')
                    : (language !== 'ko' ? 'Send' : '보내기')}
            </span>
          </button>
          {/* Search button - always visible; disabled in Phase 1 with unlock hint */}
          <button
            ref={searchBtnRef}
            onClick={() => {
              if (isPhase1) {
                // G3: Shake feedback on locked search tap
                const btn = searchBtnRef.current;
                if (btn) {
                  btn.classList.remove('shake');
                  void btn.offsetWidth; // force reflow
                  btn.classList.add('shake');
                }
                // Show visible tooltip briefly
                if (searchTipTimerRef.current) clearTimeout(searchTipTimerRef.current);
                setShowSearchTip(true);
                searchTipTimerRef.current = setTimeout(() => setShowSearchTip(false), 2500);
                return;
              }
              logSearch();
              trackPageView('web-search');
              onSearch();
            }}
            disabled={isSearching || dailySearchExhausted}
            className={`input-action-button${isPhase1 ? ' input-action-button--locked' : ''}`}
            aria-describedby={isPhase1 ? 'search-unlock-hint' : undefined}
            data-testid="search-btn"
          >
            {isSearching ? (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="input-action-icon spinning">
                <circle cx="12" cy="12" r="10"/>
                <path d="M12 6v6l4 2"/>
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="input-action-icon">
                <circle cx="11" cy="11" r="8"/>
                <line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
            )}
            <span className="btn-text">
              {isPhase1
                ? (language !== 'ko' ? `Search (${profileCompletion}%)` : `검색 (${profileCompletion}%)`)
                : isSearching
                  ? (language !== 'ko' ? 'Searching...' : '검색 중...')
                  : (language !== 'ko' ? 'Search' : '검색')
              }
            </span>
            {showSearchTip && isPhase1 && (
              <span className="search-unlock-tooltip" role="tooltip">
                {language !== 'ko'
                  ? `Complete ${Math.max(50 - profileCompletion, 0)}% more to unlock 🔓`
                  : `${Math.max(50 - profileCompletion, 0)}% 더 채우면 잠금 해제 🔓`}
              </span>
            )}
          </button>
        </>
      )}
      
      {/* Voice button - always visible if supported */}
      {isVoiceSupported && onVoiceToggle && (
        <div className="voice-button-wrapper">
          <VoiceButton
            status={voiceState?.status || 'disconnected'}
            onClick={handleVoiceToggle}
            isSupported={isVoiceSupported}
            size="medium"
            error={voiceState?.error}
          />
          {showVoiceTip && (
            <span className="voice-tip" role="tooltip">
              {language !== 'ko' ? '✨ Try voice chat!' : '✨ 음성 대화 해보세요!'}
            </span>
          )}
        </div>
      )}
      {/* Hidden hint for aria-describedby on locked search button */}
      {isPhase1 && (
        <span id="search-unlock-hint" className="sr-only">
          {language !== 'ko'
            ? `Complete your profile to unlock search (${profileCompletion}% → 50%)`
            : `프로필을 완성하면 검색이 열립니다 (${profileCompletion}% → 50%)`}
        </span>
      )}
    </div>
  );
}

export default ChatInput;
